/*
 * CSS for the shadow-DOM-isolated dialog.
 *
 * Colors are aliased once on :host from the host site's palette tokens
 * (--clr-base, --clr-contrast, --clr-accent-1, --clr-danger-*, etc.) with
 * light-theme fallbacks for sites without the palette. The aliases use a
 * --_ prefix so they don't collide with anything inherited from the host.
 * Custom properties inherit through the shadow boundary and survive the
 * host element's `all: initial`, so this Just Works on Scalebloom client
 * sites that define the palette.
 *
 * Theme switching (light ↔ dark) is driven by the host site swapping its
 * own --clr-* values — there's no prefers-color-scheme handling here.
 * var() resolution is dynamic, so the aliases re-evaluate on theme change.
 *
 */

:host {
  --_base: var(--clr-base, hwb(0 97% 3%));
  --_base-2: var(--clr-base-2, hwb(218 91% 5%));
  --_base-3: var(--clr-base-3, hwb(0 84% 16%));
  --_contrast: var(--clr-contrast, hwb(0 11% 89%));
  --_contrast-3: var(--clr-contrast-3, hwb(0 33% 67%));
  --_accent-1: var(--clr-accent-1, hwb(231 13% 0%));
  --_accent-2: var(--clr-accent-2, hwb(201 57% 0%));
  --_danger-base: var(--clr-danger-base, #ffc0ca);
  --_danger-contrast: var(--clr-danger-contrast, #8f0015);
}

.dialog {
  background: var(--_base);
  color: var(--_contrast);
  font: inherit;
  width: min(calc(100vw - 2rem), 32rem);
  height: min(80vh, 36rem);
  border: 1px solid var(--_base-3);
  border-radius: 16px;
  margin: auto auto 1rem;
  padding: 0;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.25);
  /* Enter/leave animation. display and overlay both need allow-discrete so
     the dialog can transition between top-layer and not. @starting-style
     gives the entry frame; the base rules above are the exit (closed) frame. */
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 180ms ease,
    transform 180ms ease,
    overlay 180ms ease allow-discrete,
    display 180ms ease allow-discrete;

  @media (min-width: 768px) {
    margin: auto;
  }

  /* display: flex repeats in [open] and @starting-style on purpose — display
     is in the transition list, so each state must set it explicitly or the
     dialog flashes to display: block mid-transition. */
  &[open] {
    display: flex;
    opacity: 1;
    transform: translateY(0);
    @starting-style {
      display: flex;
      opacity: 0;
      transform: translateY(8px);
    }
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.35);
    opacity: 0;
    transition:
      opacity 180ms ease,
      overlay 180ms ease allow-discrete,
      display 180ms ease allow-discrete;
  }
  &[open]::backdrop {
    opacity: 1;
    @starting-style {
      opacity: 0;
    }
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid var(--_base-3);
  flex-shrink: 0;
}
.title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;

  &::before {
    content: "";
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--_accent-1);
  }
}
.close {
  background: none;
  border: none;
  padding: 0.375rem;
  border-radius: 8px;
  color: var(--_contrast);
  cursor: pointer;
  display: grid;
  place-items: center;
  font: inherit;

  &:hover {
    background: var(--_base-2);
  }
  & svg {
    width: 1.125rem;
    height: 1.125rem;
  }
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.empty {
  color: var(--_contrast-3);
  text-align: center;
  margin: auto 0;
  padding: 1.25rem 0;
}

.starters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0 1.25rem 1rem;
}
.starter {
  border: 1px solid var(--_base-3);
  background: var(--_base-2);
  color: var(--_contrast);
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;

  &:hover {
    border-color: var(--_accent-1);
  }
}

.message {
  max-width: 85%;
  padding: 0.625rem 0.875rem;
  border-radius: 12px;
  font-size: 0.9375rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.message-user {
  align-self: flex-end;
  background: var(--_accent-2);
  color: var(--_contrast);
}
.message-assistant {
  align-self: flex-start;
  background: var(--_base-2);
  color: var(--_contrast);
}
.message-error {
  align-self: center;
  background: var(--_danger-base);
  color: var(--_danger-contrast);
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
}

.form {
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid var(--_base-3);
  flex-shrink: 0;
}
/* Off-screen rather than display:none so bots that skip hidden fields still
   find and fill it. Selected by attribute (no telltale class name). */
.form input[name="website"] {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.input {
  flex: 1;
  resize: none;
  border: 1px solid var(--_base-3);
  border-radius: 12px;
  background: var(--_base);
  color: var(--_contrast);
  padding: 0.625rem 0.875rem;
  font: inherit;
  font-size: 0.9375rem;
  line-height: 1.4;
  max-height: 8rem;
  field-sizing: content;

  &:focus {
    outline: none;
    border-color: var(--_accent-1);
  }
}
.send {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  background: var(--_accent-1);
  color: var(--_base);
  border: none;
  border-radius: 12px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  & svg {
    width: 1.125rem;
    height: 1.125rem;
  }
}
